import { useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { Layout, Menu, Avatar, Dropdown, Space, Typography, App, theme } from 'antd';
import { useScrollToTop } from '../hooks/useScrollToTop';
import type { MenuProps } from 'antd';
import {
  DashboardOutlined,
  UserOutlined,
  LogoutOutlined,
  MenuOutlined,
  TeamOutlined,
  SafetyOutlined,
  BankOutlined,
  CreditCardOutlined,
  NotificationOutlined,
  UserSwitchOutlined,
  AuditOutlined,
  LeftCircleOutlined,
  RightCircleOutlined,
  SwapOutlined,
  DollarOutlined,
  TagOutlined,
  ToolOutlined,
  LineChartOutlined,
} from '@ant-design/icons';
import { useAdminAuthStore } from '../stores/adminAuthStore';
import type { AdminRole, AdminCompanyScope } from '../stores/adminAuthStore';
import { adminLoginPath, adminPath } from '../lib/adminPaths';
import { useResponsive, LAYOUT_CONSTANTS } from '../hooks/useResponsive';
import { MobileDrawerMenu } from '../components/layout/MobileDrawerMenu';
import { FONT_FAMILY_BASE } from '../constants/fonts';
import { AppBreadcrumb, BreadcrumbProvider } from '../components/Breadcrumb';
import { featureFlags } from '../utils/featureFlags';
import { DevRouteInfo } from '../components/common/DevRouteInfo';

const { Header, Sider, Content } = Layout;
const { Text } = Typography;
const { useToken } = theme;

// 環境判定とスタイル
type EnvType = 'production' | 'staging' | 'local';

function getEnvironment(): EnvType {
  const hostname = window.location.hostname;
  if (hostname === 'ichigen-cho.com' || hostname.endsWith('.ichigen-cho.com')) {
    return 'production';
  }
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return 'local';
  }
  return 'staging';
}

const envStyles: Record<EnvType, { background: string; label: string; menuTint: string }> = {
  production: {
    background: 'linear-gradient(135deg, #2C3E50 0%, #1a252f 100%)',
    label: '本番',
    menuTint: 'rgba(44, 62, 80, 0.03)', // ほぼ透明（本番は控えめ）
  },
  staging: {
    background: 'linear-gradient(135deg, #e67e22 0%, #d35400 100%)',
    label: 'STG',
    menuTint: 'rgba(230, 126, 34, 0.08)', // 薄いオレンジ
  },
  local: {
    background: 'linear-gradient(135deg, #27ae60 0%, #1e8449 100%)',
    label: 'LOCAL',
    menuTint: 'rgba(39, 174, 96, 0.08)', // 薄い緑
  },
};

const roleLabelMap: Record<AdminRole, string> = {
  system_admin: 'システム管理者',
  company_admin: '会社管理者',
  customer_success: 'カスタマーサクセス',
  finance: '財務担当',
  agency_manager: '代理店管理者',
};

const scopeLabelMap: Record<AdminCompanyScope, string> = {
  all: '全社',
  company: '企業',
  assigned: '担当範囲',
};

export function AdminLayout() {
  const { message } = App.useApp();
  const navigate = useNavigate();
  const location = useLocation();
  const { token } = useToken();
  const [collapsed, setCollapsed] = useState(false);
  const { admin, logout } = useAdminAuthStore();

  // 環境情報
  const env = getEnvironment();
  const envStyle = envStyles[env];
  const hostname = window.location.hostname;

  // レスポンシブ対応
  const { isMobile, useMobileMenu } = useResponsive();
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const [openKeys, setOpenKeys] = useState<string[]>([]);

  // ページ遷移時にスクロール位置をトップにリセット
  const contentRef = useScrollToTop<HTMLDivElement>();

  const handleLogout = () => {
    logout();
    message.success('ログアウトしました');
    navigate(adminLoginPath);
  };

  // フラットなメニュー構造（グループ表示）
  const menuItems: MenuProps['items'] = [
    // メイン
    {
      key: adminPath('dashboard'),
      icon: <DashboardOutlined />,
      label: 'ダッシュボード',
    },

    // 会社・サブスク管理
    {
      type: 'group',
      label: '会社・サブスク管理',
      children: [
        { key: adminPath('companies'), icon: <BankOutlined />, label: '会社管理' },
        { key: adminPath('subscriptions'), icon: <CreditCardOutlined />, label: '契約一覧' },
        // 銀行振込管理はフラグで制御
        ...(featureFlags.bankTransfer
          ? [{ key: adminPath('subscriptions/bank-transfers'), icon: <SwapOutlined />, label: '振込管理' }]
          : []),
        { key: adminPath('subscriptions/plan-master'), icon: <TagOutlined />, label: 'プラン管理' },
        { key: adminPath('subscriptions/coupons'), icon: <DollarOutlined />, label: 'クーポン管理' },
      ],
    },

    // 代理店管理
    {
      type: 'group',
      label: '代理店管理',
      children: [
        { key: adminPath('agencies'), icon: <TeamOutlined />, label: '代理店一覧' },
        { key: adminPath('agencies/commissions'), icon: <DollarOutlined />, label: '手数料管理' },
      ],
    },

    // パートナー管理
    {
      type: 'group',
      label: 'パートナー管理',
      children: [
        { key: adminPath('partners'), icon: <TeamOutlined />, label: 'パートナー一覧' },
      ],
    },

    // お知らせ・ユーザー
    {
      type: 'group',
      label: 'お知らせ・ユーザー',
      children: [
        { key: adminPath('announcements'), icon: <NotificationOutlined />, label: 'お知らせ配信' },
        { key: adminPath('users'), icon: <TeamOutlined />, label: 'ユーザー管理' },
      ],
    },

    // システム管理
    {
      type: 'group',
      label: 'システム管理',
      children: [
        { key: adminPath('admin-users'), icon: <UserSwitchOutlined />, label: '管理ユーザー' },
        { key: adminPath('audit-log'), icon: <AuditOutlined />, label: '監査ログ' },
        { key: adminPath('user-activity'), icon: <LineChartOutlined />, label: 'ユーザー活動分析' },
        { key: adminPath('settings/access-templates'), icon: <SafetyOutlined />, label: 'アクセス権テンプレ' },
        { key: adminPath('settings/maintenance'), icon: <ToolOutlined />, label: 'メンテナンス' },
      ],
    },
  ];

  // メニュークリック時のハンドラー
  const handleMenuClick = (key: string) => {
    navigate(key);
  };

  // サイドバーの幅計算（モバイル時は0）
  const sidebarWidth = useMobileMenu ? 0 : (collapsed ? LAYOUT_CONSTANTS.SIDEBAR_COLLAPSED_WIDTH : LAYOUT_CONSTANTS.SIDEBAR_WIDTH);
  const contentPadding = isMobile ? LAYOUT_CONSTANTS.CONTENT_PADDING_MOBILE : 24;
  const headerHeight = isMobile ? LAYOUT_CONSTANTS.HEADER_HEIGHT_MOBILE : LAYOUT_CONSTANTS.HEADER_HEIGHT;

  const userMenuItems = [
    {
      key: 'profile',
      icon: <UserOutlined />,
      label: '管理者情報',
      onClick: () => navigate(adminPath('profile')),
      disabled: true,
    },
    {
      key: 'divider',
      type: 'divider' as const,
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: 'ログアウト',
      onClick: handleLogout,
    },
  ];

  return (
    <Layout style={{ height: '100vh', overflow: 'hidden' }}>
      {/* モバイル用ドロワーメニュー */}
      {useMobileMenu && (
        <MobileDrawerMenu
          open={mobileDrawerOpen}
          onClose={() => setMobileDrawerOpen(false)}
          menuItems={menuItems}
          selectedKeys={[location.pathname]}
          openKeys={openKeys}
          onOpenChange={(keys) => setOpenKeys(keys)}
          onMenuClick={handleMenuClick}
          title="管理者"
          subtitle={`ADMIN PORTAL | ${envStyle.label} | ${hostname}`}
          headerBackground={envStyle.background}
        />
      )}

      {/* デスクトップ用サイドバー */}
      {!useMobileMenu && (
        <Sider
          collapsible
          collapsed={collapsed}
          onCollapse={setCollapsed}
          trigger={null}
          theme="light"
          breakpoint="lg"
          style={{
            height: '100vh',
            position: 'fixed',
            left: 0,
            top: 0,
            bottom: 0,
            zIndex: 100,
            overflow: 'hidden',
          }}
        >
          <div style={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
          }}>
            {/* タイトル部分（固定） */}
            <div style={{
              height: 64,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderBottom: `1px solid ${token.colorBorderSecondary}`,
              background: envStyle.background,
              padding: collapsed ? '12px 8px' : '12px 16px',
              flexShrink: 0,
            }}>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#fff',
                  fontWeight: 700,
                  lineHeight: 1.05,
                  textAlign: 'center',
                  gap: collapsed ? 2 : 4,
                  fontFamily: FONT_FAMILY_BASE,
                  transition: 'all 0.2s ease',
                }}
              >
                <span
                  style={{
                    fontSize: collapsed ? 16 : 18,
                    letterSpacing: '0.05em',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {collapsed ? '管理' : '一元帳 管理'}
                </span>
                <span
                  style={{
                    fontSize: collapsed ? 7 : 9,
                    letterSpacing: '-0.0em',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {collapsed ? 'ADMIN' : 'ADMIN PORTAL'}
                </span>
                {/* 環境情報表示 */}
                <span
                  style={{
                    fontSize: collapsed ? 6 : 8,
                    opacity: 0.8,
                    marginTop: collapsed ? 0 : 2,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {collapsed ? envStyle.label : `${envStyle.label} | ${hostname}`}
                </span>
              </div>
            </div>

            {/* メニュー部分（スクロール可能） */}
            <div
              className="admin-sidebar-menu-scroll"
              style={{
                flex: 1,
                overflowY: 'auto',
                overflowX: 'hidden',
                background: envStyle.menuTint,
              }}
            >
              <Menu
                theme="light"
                mode="inline"
                selectedKeys={[location.pathname]}
                openKeys={openKeys}
                onOpenChange={(keys) => setOpenKeys(keys as string[])}
                items={menuItems}
                onClick={({ key }) => navigate(key)}
                style={{
                  borderRight: 0,
                  paddingTop: 8,
                  background: 'transparent',
                }}
              />
            </div>
          </div>
          <style
            dangerouslySetInnerHTML={{
              __html: `
              .ant-menu-submenu > .ant-menu-submenu-title {
                font-weight: 500 !important;
              }
              .ant-menu-item {
                margin-bottom: 1px !important;
              }
              .ant-menu-submenu {
                margin-bottom: 1px !important;
              }
              /* グループヘッダーのスタイル */
              .ant-menu-item-group-title {
                font-size: 11px !important;
                color: ${token.colorTextSecondary} !important;
                padding: 8px 16px 2px !important;
                text-transform: uppercase !important;
                letter-spacing: 0.5px !important;
                border-top: 1px solid ${token.colorBorderSecondary} !important;
                margin-top: 4px !important;
              }
              /* 最初のグループは上線なし */
              .ant-menu-item-group:first-child .ant-menu-item-group-title {
                border-top: none !important;
                margin-top: 0 !important;
              }
              /* スクロールバーを非表示 */
              .admin-sidebar-menu-scroll {
                -ms-overflow-style: none !important;
                scrollbar-width: none !important;
              }
              .admin-sidebar-menu-scroll::-webkit-scrollbar {
                display: none !important;
                width: 0 !important;
                height: 0 !important;
              }
              /* Sider内部のレイアウト調整 */
              .ant-layout-sider-children {
                display: flex !important;
                flex-direction: column !important;
                height: 100% !important;
              }
            `
            }}
          />
        </Sider>
      )}

      <Layout style={{
        marginLeft: sidebarWidth,
        transition: 'margin-left 0.2s',
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        background: '#EEEEEE',
      }}>
        <Header style={{
          padding: `0 ${contentPadding}px`,
          background: '#fff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          boxShadow: '0 1px 4px rgba(0,21,41,.08)',
          position: 'fixed',
          top: 0,
          right: 0,
          left: sidebarWidth,
          height: headerHeight,
          lineHeight: `${headerHeight}px`,
          zIndex: 99,
          transition: 'left 0.2s',
        }}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            {/* モバイル: ハンバーガーメニュー */}
            {useMobileMenu ? (
              <MenuOutlined
                style={{ fontSize: 20, cursor: 'pointer', padding: 8 }}
                onClick={() => setMobileDrawerOpen(true)}
              />
            ) : (
              /* デスクトップ: サイドバー折り畳みトグル */
              collapsed ? (
                <RightCircleOutlined
                  style={{ fontSize: 18, cursor: 'pointer' }}
                  onClick={() => setCollapsed(false)}
                />
              ) : (
                <LeftCircleOutlined
                  style={{ fontSize: 18, cursor: 'pointer' }}
                  onClick={() => setCollapsed(true)}
                />
              )
            )}
          </div>

          <Space size={isMobile ? 8 : 16} align="center">
            {/* モバイルでは詳細情報を非表示 */}
            {!isMobile && (
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-end',
                minWidth: 120,
                maxWidth: 200,
              }}>
                <Text
                  strong
                  style={{
                    fontSize: 13,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    maxWidth: '100%',
                  }}
                >
                  {admin?.name ?? admin?.email ?? '管理者'}
                </Text>
                <Text
                  type="secondary"
                  style={{
                    fontSize: 11,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {admin ? roleLabelMap[admin.role] : '---'} / {admin ? scopeLabelMap[admin.companyScope] : '---'}
                </Text>
              </div>
            )}
            <Dropdown menu={{ items: userMenuItems }} trigger={['click']} placement="bottomRight">
              <Avatar
                size={isMobile ? 'default' : 'default'}
                icon={<UserOutlined />}
                style={{
                  backgroundColor: '#2C3E50',
                  cursor: 'pointer',
                }}
              />
            </Dropdown>
          </Space>
        </Header>

        <Content
          ref={contentRef}
          style={{
            marginTop: headerHeight,
            padding: contentPadding,
            overflowY: 'auto',
            height: `calc(100vh - ${headerHeight}px)`,
            background: '#EEEEEE',
          }}
        >
          <div style={{
            background: '#fff',
            borderRadius: isMobile ? 4 : 8,
            padding: contentPadding,
            minHeight: `calc(100vh - ${headerHeight + contentPadding * 2}px)`,
            boxShadow: '0 1px 4px rgba(0,21,41,.08)',
          }}>
            <BreadcrumbProvider>
              <AppBreadcrumb layoutType="admin" />
              <Outlet />
            </BreadcrumbProvider>
          </div>
        </Content>

        {/* 開発環境でのみ表示されるファイルパス情報 */}
        <DevRouteInfo />
      </Layout>
    </Layout>
  );
}
